import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v8';
import { ErrorBoundary } from 'react-error-boundary';
import { createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { Toaster } from 'sonner';

import { AppShell } from '@/components/appShell';
import { AuthGate } from '@/components/authGate';
import { ErrorState } from '@/components/stateViews';
import { AnalyticsAuthProvider } from '@/lib/authContext';
import {
  ExploreCompatibilityPage,
  LegacyRouteRedirect,
  PerformanceCompatibilityPage,
  SystemCompatibilityPage,
  UsageCompatibilityPage,
} from '@/pages/compatibilityRoutes';
import { FailuresPage } from '@/pages/failuresPage';
import { FrontendExperiencePage } from '@/pages/frontendExperiencePage';
import { NotFoundPage } from '@/pages/notFoundPage';
import { OverviewPage } from '@/pages/overviewPage';
import { ServicesPage } from '@/pages/servicesPage';
import { UsersPage } from '@/pages/usersPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const router = createBrowserRouter([
  {
    children: [
      { element: <OverviewPage />, index: true },
      { element: <FailuresPage />, path: 'failures' },
      { element: <PerformanceCompatibilityPage />, path: 'performance' },
      { element: <FrontendExperiencePage />, path: 'frontend-experience' },
      { element: <UsageCompatibilityPage />, path: 'usage' },
      { element: <ExploreCompatibilityPage />, path: 'explore' },
      { element: <SystemCompatibilityPage />, path: 'system' },
      { element: <ServicesPage />, path: 'services' },
      { element: <UsersPage />, path: 'users' },
      { element: <LegacyRouteRedirect pathname="/explore" />, path: 'query' },
      { element: <LegacyRouteRedirect pathname="/performance" view="runs" />, path: 'runs' },
      { element: <LegacyRouteRedirect pathname="/performance" view="timing" />, path: 'timing' },
      { element: <LegacyRouteRedirect pathname="/performance" view="startup" />, path: 'startup' },
      { element: <LegacyRouteRedirect pathname="/frontend-experience" />, path: 'rum-sessions' },
      { element: <LegacyRouteRedirect pathname="/system" view="definitions" />, path: 'catalog' },
      { element: <LegacyRouteRedirect pathname="/usage" view="inventory" />, path: 'inventory' },
      { element: <LegacyRouteRedirect pathname="/system" view="collection" />, path: 'health' },
      { element: <LegacyRouteRedirect pathname="/explore" view="logs" />, path: 'logs' },
      { element: <NotFoundPage />, path: '*' },
    ],
    element: (
      <NuqsAdapter>
        <AuthGate>
          <AppShell />
        </AuthGate>
      </NuqsAdapter>
    ),
  },
]);

export const App = () => (
  <ErrorBoundary fallbackRender={({ error }) => <ErrorState message={error instanceof Error ? error.message : 'Analytics app failed'} />}>
    <QueryClientProvider client={queryClient}>
      <AnalyticsAuthProvider>
        <RouterProvider router={router} />
      </AnalyticsAuthProvider>
      <Toaster richColors />
    </QueryClientProvider>
  </ErrorBoundary>
);
